import React from 'react';
import i18next from 'i18next';
import type { IInitSettings } from '../init';
import { useSelector } from 'react-redux';
import { eplassSliceDocumentUrl, eplassSliceViewMode } from '../Redux/EplassSlice';
import WebViewer, { type WebViewerInstance } from '@pdftron/webviewer';

import styles from './PdfViewer.module.scss';
import { GlobalDialogs } from '../pdf/GlobalDialogs';
import { ReduxUpdater } from '../pdf/ReduxUpdater';
import { checkExtension, devLog, signatureArrayLength, upperCaseBegin } from '../utils';
import { PdfViewerViewModeFlag } from '../pdf/Enums';
import { ApryseUpdater } from '../pdf/ApryseUpdater';
import { ApryseSDK } from '../pdf/ApryseSDK';
import { backendApiUrl, onWindowKeyDown } from '../App';
import { GlobalConstants } from '../pdf';
import { GlobalVariables } from '../pdf/GlobalVariables';
import { ToastSeverity } from './General/Toast/Toast';

/**
 * Properties for the PdfViewer component.
 */
export type IPdfViewerProps = Pick<
    IInitSettings & {
        className: string;
        customFontURL?: string;
        onPdfOpened: () => void;
        onPdfError: (error: Error) => void;
    },
    'className' | 'customFontURL' | 'lic' | 'onPdfOpened' | 'onPdfError' | 'testRun'
>;

const PdfViewer: React.FC<IPdfViewerProps> = props => {
    const { className, lic, onPdfOpened, onPdfError, customFontURL } = props;
    const documentUrl = useSelector(eplassSliceDocumentUrl);
    const viewMode = useSelector(eplassSliceViewMode);

    const elRef = React.useRef<HTMLDivElement>(null);
    const handleError = (err: Error) =>
        GlobalDialogs.showNotyDebounced(2.5, err.message, ToastSeverity.Error);

    const onUnhandledRejection = (e: PromiseRejectionEvent): void => {
        handleError(e.reason || new Error(i18next.t('errors.init')));
    };

    const blockContextMenu = (e: Event): void => {
        e.preventDefault();
    };

    // Handle errors during initialization
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    document.addEventListener('contextmenu', blockContextMenu);
    const teardownErrorHandlers = (): void => {
        window.removeEventListener('unhandledrejection', onUnhandledRejection);
        document.removeEventListener('contextmenu', blockContextMenu);
    };

    const processDigitalSignatures = async () => {
        if (process.env.REACT_APP_SIGNATURE_CERTIFICATES) {
            const certificates = process.env.REACT_APP_SIGNATURE_CERTIFICATES.split('|');
            setTimeout(async () => {
                await ApryseSDK.verifyAllDigitalSignatures(certificates);
            }, 750);
        }
    };

    const initPdfViewerInstance = () => {
        const element = document.getElementById('viewer');
        if (element) {
            GlobalVariables.licenseKey = lic;
            WebViewer(
                {
                    fullAPI: true,
                    initialDoc:
                        GlobalVariables.isVisualStudioCode && ReduxUpdater.documentUrl2 === ''
                            ? ReduxUpdater.documentUrl
                            : '', // path to your document
                    licenseKey: lic,
                    path: '/', // point to where the files you copied are served from
                    backendType: 'ems',
                },
                element,
            )
                .then(instance => {
                    if (ReduxUpdater.documentUrl2 === '') {
                        loadPdf(instance);
                    }

                    const { annotationManager, documentViewer, PDFNet } = instance.Core;
                    instance.UI.disableElements([
                        'downloadButton',
                        'printButton',
                        'highlightToolGroupButton',
                        'underlineToolGroupButton',
                        'strikeoutToolGroupButton',
                        'squigglyToolGroupButton',
                        'stickyToolGroupButton',
                        'freeTextToolGroupButton',
                        'shapeToolGroupButton',
                        'freeHandToolGroupButton',
                        'freeHandHighlightToolGroupButton',
                        'eraserToolButton',
                        'viewControlsButton',
                        'leftPanelButton',
                        'searchButton',
                        'menuButton',
                        'pageNavOverlay',
                        'header',
                    ]);
                    instance.UI.Fonts.setAnnotationFonts([GlobalConstants.default.fontFamily]);
                    if (customFontURL) {
                        devLog('setCustomFontURL: ', customFontURL);
                        instance.Core.setCustomFontURL(customFontURL);
                    }
                    ApryseSDK.annotationManager = annotationManager;
                    ApryseSDK.documentViewer = documentViewer;
                    ApryseSDK.instance = instance;

                    if (ReduxUpdater.documentUrl2 !== '') {
                        instance.UI.addEventListener('viewerLoaded', async () => {
                            await ApryseSDK.pdfNet.initialize();

                            const newDoc = await ApryseSDK.generateDocumentDiff(
                                ReduxUpdater.documentUrl.endsWith('.pdf')
                                    ? ReduxUpdater.documentUrl
                                    : backendApiUrl.pdf.open.replace(
                                        '{documentId}',
                                        ReduxUpdater.documentUrl,
                                    ),
                                ReduxUpdater.documentUrl2.endsWith('.pdf')
                                    ? ReduxUpdater.documentUrl2
                                    : backendApiUrl.pdf.open.replace(
                                        '{documentId}',
                                        ReduxUpdater.documentUrl2,
                                    ),
                            );

                            // display the document!
                            // instance is a global variable thats automatically defined inside the config file.
                            instance.UI.loadDocument(newDoc);
                        });
                    }

                    documentViewer.addEventListener('keyDown', onWindowKeyDown);
                    documentViewer.addEventListener('documentLoaded', async () => {
                        devLog('[event] documentLoaded...');
                        ApryseSDK._documentLoaded = true;
                        ApryseSDK._startTime = Date.now();

                        await PDFNet.initialize();
                        ApryseSDK.doc = ApryseSDK.documentViewer.getDocument();
                        ApryseSDK.pdfDoc = await ApryseSDK.documentViewer.getDocument().getPDFDoc();
                        ApryseSDK.undoManager = await ApryseSDK.pdfDoc.getUndoManager();
                        ApryseSDK.setToolMode(ApryseSDK.ToolsENUM.DEFAULT);
                        localStorage.setItem(
                            GlobalConstants.editingSessionTimestamp,
                            new Date().getTime().toString(),
                        );

                        ApryseSDK.adjustAnnotations();
                        onPdfOpened();
                        await processDigitalSignatures();
                    });
                    documentViewer.addEventListener(
                        'pageComplete',
                        (pageNumber: number, canvas: HTMLCanvasElement) => {
                            ApryseSDK.pageCanvases[pageNumber] = canvas;
                        },
                    );
                    documentViewer.addEventListener('pageNumberUpdated', (pageNumber: number) => {
                        ApryseUpdater.currentPage = pageNumber;
                    });
                    documentViewer.addEventListener(
                        'pageComplete',
                        (pageNumber: number, canvas: HTMLCanvasElement) => {
                            ApryseSDK.pageCanvases[pageNumber] = canvas;
                        },
                    );
                    documentViewer.addEventListener('zoomUpdated', (zoom: number) => {
                        if (isNaN(zoom)) {
                            // Fix for zoom crashing
                            zoom = ApryseUpdater.currentScale * 0.99;
                            GlobalVariables.zoom(zoom);
                        }
                        ApryseUpdater.currentScale = zoom;
                    });
                })
                .catch(err => onPdfError(err));
        }
    };

    const loadPdf = async (instance: WebViewerInstance) => {
        if (GlobalVariables.isVisualStudioCode) {
            return;
        }
        try {
            const url = ReduxUpdater.documentUrl.endsWith('.pdf')
                ? backendApiUrl.pdf.openlocal.replace('{documentUrl}', ReduxUpdater.documentUrl)
                : backendApiUrl.pdf.open.replace('{documentId}', ReduxUpdater.documentUrl);

            const fileResponse = await fetch(url);

            if (fileResponse.status != 200) {
                onPdfError(new Error('PDF could not be loaded.'));

                return;
            }

            const file = await fileResponse.blob();

            if (file.size <= 0) {
                onPdfError(new Error('PDF could not be loaded.'));

                return;
            }

            const extension = await checkExtension(file.slice(0, signatureArrayLength()), () => {
                ReduxUpdater.viewMode = PdfViewerViewModeFlag.Images;
            });

            instance.UI.loadDocument(file, { extension });
            return;
        } catch (error) {
            onPdfError(error as Error);
        }
    };

    const cssClasses = React.useMemo(() => {
        if (viewMode === PdfViewerViewModeFlag.Annotation) {
            ApryseSDK.enableApryseUiElements();
        } else {
            ApryseSDK.disableApryseUiElements();
        }

        return [
            styles.viewer,
            'pdf-viewer',
            className,
            `appIsIn${upperCaseBegin(viewMode.toString())}`,
        ];
    }, [viewMode]);

    // Setup/Teardown viewer instance
    React.useEffect(() => {
        ReduxUpdater.viewMode = PdfViewerViewModeFlag.Loading;
        initPdfViewerInstance();

        return () => {
            teardownErrorHandlers();
        };
    }, [documentUrl /*, openAttempts*/]);

    return (
        <div id={'pdfViewerControl'} className={cssClasses.join(' ')} ref={elRef}>
            <div id="viewer" className={styles.fullHeight}></div>
        </div>
    );
};

/**
 * The PDF Viewer component.
 */
export default PdfViewer;
